import React from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { GraduationCap, CheckCircle, XCircle, Clock } from 'lucide-react';
import { format } from 'date-fns';

export default function TrainingManagement({ isCommander, currentUserEmail }) {
  const queryClient = useQueryClient();
  const [reviewingRequest, setReviewingRequest] = React.useState(null);
  const [reviewNotes, setReviewNotes] = React.useState('');

  const { data: trainingRequests } = useQuery({
    queryKey: ['trainingRequests'],
    queryFn: () => base44.entities.TrainingRequest.list(),
    initialData: []
  });

  const { data: officers } = useQuery({
    queryKey: ['officers'],
    queryFn: () => base44.entities.OfficerProfile.list(),
    initialData: []
  });

  const reviewMutation = useMutation({
    mutationFn: ({ id, approved, notes }) => {
      const updates = {
        status: approved ? 'approved' : 'rejected',
        reviewed_by: currentUserEmail,
        reviewed_at: new Date().toISOString(),
        review_notes: notes
      };
      return base44.entities.TrainingRequest.update(id, updates);
    },
    onSuccess: async (result, variables) => {
      if (variables.approved) {
        // Find the request and officer to update skills
        const request = trainingRequests.find(r => r.id === variables.id);
        const officer = officers.find(o => o.user_email === request.officer_email);
        
        if (officer && request) {
          const updatedSkills = [...(officer.skills || [])];
          const skillIndex = updatedSkills.findIndex(s => s.name === request.skill_name);
          
          if (skillIndex >= 0) {
            const newXp = (updatedSkills[skillIndex].xp || 0) + request.sp_requested;
            updatedSkills[skillIndex] = {
              ...updatedSkills[skillIndex],
              xp: newXp,
              level: request.target_level
            };
            
            await base44.entities.OfficerProfile.update(officer.id, { skills: updatedSkills });
          }
        }
      }
      
      queryClient.invalidateQueries({ queryKey: ['trainingRequests'] });
      queryClient.invalidateQueries({ queryKey: ['officers'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      setReviewingRequest(null);
      setReviewNotes('');
    }
  });

  const myRequests = trainingRequests.filter(r => r.officer_email === currentUserEmail);
  const pendingRequests = isCommander ? trainingRequests.filter(r => r.status === 'pending') : [];

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-amber-500/20 text-amber-400', icon: Clock },
      approved: { color: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle },
      rejected: { color: 'bg-red-500/20 text-red-400', icon: XCircle },
      completed: { color: 'bg-indigo-500/20 text-indigo-400', icon: CheckCircle }
    };
    const { color, icon: Icon } = config[status] || config.pending;
    
    return (
      <Badge className={`${color} border-none flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {status}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* My Training Requests */}
      <Card className="glass-panel border-none">
        <CardHeader>
          <CardTitle className="text-slate-700 font-semibold tracking-tight leading-none flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-indigo-400" />
            My Training Requests
          </CardTitle>
        </CardHeader>
        <CardContent>
          {myRequests.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No training requests submitted yet
            </div>
          ) : (
            <div className="space-y-3">
              {myRequests.map((request) => (
                <div key={request.id} className="p-4 bg-slate-950 rounded-lg border border-slate-800">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className="text-slate-300 font-semibold">{request.skill_name}</h4>
                      <p className="text-sm text-slate-500">
                        Level {request.current_level} → {request.target_level} ({request.sp_requested} SP)
                      </p>
                    </div>
                    {getStatusBadge(request.status)}
                  </div>
                  <p className="text-sm text-slate-400 mb-3">{request.justification}</p>
                  {request.status !== 'pending' && (
                    <div className="pt-3 border-t border-slate-800">
                      <p className="text-xs text-slate-500 mb-1">
                        Reviewed by {request.reviewed_by} on {format(new Date(request.reviewed_at), 'MMM d, yyyy')}
                      </p>
                      {request.review_notes && (
                        <p className="text-sm text-slate-400">{request.review_notes}</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Commander Review Section */}
      {isCommander && (
        <Card className="glass-panel border-none">
          <CardHeader>
            <CardTitle className="text-slate-700 font-semibold tracking-tight leading-none flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
              Pending Training Approvals
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pendingRequests.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No pending training requests
              </div>
            ) : (
              <div className="space-y-3">
                {pendingRequests.map((request) => {
                  const officer = officers.find(o => o.user_email === request.officer_email);
                  
                  return (
                    <div key={request.id} className="p-4 bg-slate-950 rounded-lg border border-slate-800">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="text-slate-300 font-semibold">
                            {officer?.callsign || request.officer_email}
                          </h4>
                          <p className="text-sm text-indigo-400">
                            {request.skill_name}: Level {request.current_level} → {request.target_level}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            Requires {request.sp_requested} SP
                          </p>
                        </div>
                        {getStatusBadge(request.status)}
                      </div>
                      
                      <p className="text-sm text-slate-400 mb-3">{request.justification}</p>
                      
                      {reviewingRequest === request.id ? (
                        <div className="space-y-3 pt-3 border-t border-slate-800">
                          <Textarea data-testid="skills-trainingmanagement-textarea"
                            value={reviewNotes}
                            onChange={(e) => setReviewNotes(e.target.value)}
                            placeholder="Add notes (optional)..."
                            className="bg-slate-900 border-slate-700 h-20"
                          />
                          <div className="flex gap-2">
                            <Button
                              onClick={() => reviewMutation.mutate({ 
                                id: request.id, 
                                approved: true, 
                                notes: reviewNotes 
                              })}
                              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Approve
                            </Button>
                            <Button
                              onClick={() => reviewMutation.mutate({ 
                                id: request.id, 
                                approved: false, 
                                notes: reviewNotes 
                              })}
                              className="flex-1 bg-red-600 hover:bg-red-700"
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              Reject
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => {
                                setReviewingRequest(null);
                                setReviewNotes('');
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Button
                          onClick={() => setReviewingRequest(request.id)}
                          variant="outline"
                          className="w-full mt-3"
                        >
                          Review Request
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}